/**
 * Dinamik rejim — qattiq ustun (rigid column) modeli.
 *
 * MANBA: Wylie & Streeter, Fluid Transients in Systems (rigid column theory);
 *        Chaudhry, Applied Hydraulic Transients, Ch. 1–2.
 *
 * TENGLAMA (siqilmas suyuqlik, elastik bo'lmagan quvur; barcha bo'laklarda bir xil Q):
 *   (1/g) · Σ_i (L_i / A_i) · dQ/dt = H_pump(Q) − H_sys(Q)
 *   H_sys da ishqalanish ishorali (v·|v|), shuning uchun Q < 0 ham fizik; lekin teskari klapan
 *   bor deb Q ≥ 0 cheklanadi (ASSUMPTION 1).
 * Integratsiya: RK4, ichki qadam ≤ 0.25/|λ|, λ = g/(ΣL/A) · |dF/dQ| (lokal chiziqlashtirish).
 *
 * ASSUMPTION:
 *   1. Teskari klapan: Q < 0 → Q = 0 (nasos o'chirilganda ustun teskari oqmaydi).
 *   2. Nasos inersiyasi (rotor tezlanishi) yo'q — speedRatio bir zumda o'rnatiladi.
 *   3. Suyuqlik siqiluvchanligi va quvur elastikligi yo'q (gidravlik zarba modellashtirilmaydi).
 *   4. Ishqalanish kvazistatsionar (turg'un f(Re) har lahzada).
 */
import { stepWithSubsteps } from '../../solvers/rk4';
import { warn, type StepResult, type Warning } from '../../types';
import { G, pipeArea } from './hydraulics';
import { pumpHead } from './pump';
import {
  flowUpperBound,
  outputsAt,
  systemContext,
  systemLosses,
  validateInputs,
  validateParams,
} from './steady';
import type {
  PumpPipelineInputs,
  PumpPipelineParams,
  PumpPipelineState,
  PumpPipelineStepOutputs,
} from './types';

/** Σ L_i/A_i, 1/m */
export function inertiaLength(p: PumpPipelineParams): number {
  let s = p.discharge.length / pipeArea(p.discharge.diameter);
  if (p.suction) s += p.suction.length / pipeArea(p.suction.diameter);
  return s;
}

export function initState(p: PumpPipelineParams, i: PumpPipelineInputs, Q0 = 0): PumpPipelineState {
  validateParams(p);
  validateInputs(i);
  return { t: 0, Q: Math.max(0, Q0) };
}

export function stepDynamic(
  p: PumpPipelineParams,
  state: PumpPipelineState,
  i: PumpPipelineInputs,
  dt: number,
): StepResult<PumpPipelineState, PumpPipelineStepOutputs> {
  validateInputs(i);
  if (!(dt > 0)) throw new Error('pump-pipeline: dt must be > 0');
  const warnings: Warning[] = [];
  const ctx = systemContext(p, i, warnings);
  const LA = inertiaLength(p);
  const closed = !Number.isFinite(ctx.Kv);

  const F = (Q: number) => pumpHead(ctx.curve, Q, ctx.r) - systemLosses(p, ctx, Q).hSystem;
  const deriv = (_t: number, y: readonly number[]): number[] => {
    const Q = y[0] as number;
    if (closed) return [0];
    const dQ = (G / LA) * F(Q);
    // Teskari klapan: Q = 0 da manfiy tezlanish yo'q
    return [Q <= 0 && dQ < 0 ? 0 : dQ];
  };

  let Q = state.Q;
  if (closed) {
    Q = 0;
  } else {
    // Eng qattiq nuqta — sarf oralig'ining yuqori chegarasi (|dF/dQ| Q bilan o'sadi)
    const qHi = Math.max(Q, flowUpperBound(p, ctx));
    const h = 1e-3 * qHi;
    const slope = Math.abs((F(qHi + h) - F(qHi - h)) / (2 * h));
    const lambda = (G / LA) * Math.max(slope, 1e-12);
    const maxSub = Math.min(dt, 0.25 / lambda);
    const y = stepWithSubsteps(deriv, state.t, [Q], dt, maxSub);
    Q = Math.max(0, y[0] as number);
    if (!Number.isFinite(Q)) throw new Error('pump-pipeline: non-finite flow (unstable step)');
  }

  const outputs = outputsAt(p, ctx, Q, warnings);
  if (closed) warn(warnings, 'pump.valveClosed', 'critical');
  const next: PumpPipelineState = { t: state.t + dt, Q };
  return {
    state: next,
    outputs: { ...outputs, t: next.t, dQdt: deriv(next.t, [Q])[0] as number },
    warnings,
  };
}
